'use es6';

import PropTypes from 'prop-types';
import { Fragment, PureComponent } from 'react';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { isEmpty } from 'underscore';
import { Flex } from 'foundations-components/components/Flex';
import { Spacer } from 'foundations-components/components/Spacer';
import { Box } from 'foundations-components/transitional/components/Box';
import { Button } from 'foundations-components/transitional/components/Button';
import { LockBadge } from 'foundations-components/transitional/components/LockBadge';
import { FormLabel } from 'foundations-components/transitional/components/FormLabel';
import { TextInput } from 'foundations-components/transitional/components/TextInput';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { NumberInput } from 'foundations-components/transitional/components/NumberInput';
import { DateInput } from 'foundations-components/transitional/components/DateInput';
import { DateRange } from 'foundations-components/transitional/components/DateRange';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { fromString, toFormattedString } from 'foundations-components/transitional/utils/SimpleDate';
import { Select } from 'foundations-components/transitional/components/Select';
import { sanitize } from 'sanitize-text/sanitizers/Sanitize';
import { config } from 'sanitize-text/sanitizers/SanitizeConfiguration';
import { decodeHTML } from 'FormsUI/utils/HTMLEntitiesUtils';
import { OPEN_DEPENDENT_FIELDS_EVENT } from 'FormsUI/constants/Events';
import { getFormatTextFromMetaData } from 'FormsUI/utils/DateUtils';
import DependentFieldKeys from 'forms-utils-lib/constants/formField/DependentFieldKeys';
import Property, { PropertyType } from 'FormsUI/lib/Property';
import createFormFieldWithOptions from 'forms-utils-lib/formField/utils/createFormFieldWithOptions';
import { DependentFieldOperator, DependentFieldOperatorsByFieldType, DependentFieldPanelType, DependentFieldValidationType, getDependentFieldOperatorLabel } from 'FormsUI/constants/DependentFields';
import { validateDependentField } from 'FormsUI/utils/DependentFields';
import { getPropertyObjectTypeId } from 'FormsUI/utils/PropertiesUtils';
import { DATE_FIELD_FORMAT } from 'FormsUI/constants/DateFormats';
import { FieldTypes } from 'ui-forms-components/constants/FieldTypeMapping';
import updateDependentFieldKey from 'forms-utils-lib/dependent-fields/updateDependentFieldKey';
import createDependentField from 'forms-utils-lib/dependent-fields/createDependentField';
import createDependentFieldFilter from 'forms-utils-lib/dependent-fields/createDependentFieldFilter';

/*
  TODO: A lot of state managment in this component is done through the state instead of the store.
  Re write to be connected with smaller presentational components.
  Also write tests.
*/
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
class DependentFieldsConfigurationNewPanel extends PureComponent {
  /* eslint-disable-next-line hubspot-dev/no-static-get-default-props */
  static get defaultProps() {
    return {
      initialDependentField: createDependentField({
        dependentFormField: {}
      })
    };
  }
  constructor(props) {
    super(props);
    const operatorSelectOptions = this.getOperatorSelectOptions();
    const dependentField = this.props.type === DependentFieldPanelType.EDIT ? this.props.initialDependentField : updateDependentFieldKey(this.props.initialDependentField, 'filters', [createDependentFieldFilter({
      operator: operatorSelectOptions[0].value
    })]);
    this.state = {
      dependentField,
      isSubmitted: false,
      validation: {}
    };
    this.formFieldsSelectRenderer = this.formFieldsSelectRenderer.bind(this);
    this.handleAddDependentField = this.handleAddDependentField.bind(this);
    this.handleOperatorChange = this.handleOperatorChange.bind(this);
    this.handleDependentFieldChange = this.handleDependentFieldChange.bind(this);
  }
  getLangKey(path) {
    return `editor.form.config.dependentFields.${path}`;
  }
  validateDependentFieldAndUpdateValidations() {
    const {
      formField
    } = this.props;
    const {
      dependentField
    } = this.state;
    const validation = validateDependentField(formField.type, dependentField);
    this.setState({
      validation
    });
    if (Object.keys(validation).length > 0) {
      return false;
    }
    return true;
  }
  handleAddDependentField() {
    const {
      type,
      addDependentFieldFilter,
      updateDependentFieldFilter
    } = this.props;
    const {
      dependentField
    } = this.state;
    this.setState({
      isSubmitted: true
    });
    const isDependentFieldValid = this.validateDependentFieldAndUpdateValidations(dependentField);
    if (!isDependentFieldValid) {
      return;
    }
    if (type === DependentFieldPanelType.NEW) {
      addDependentFieldFilter(dependentField);
      this.triggerOpenEvent();
    } else if (type === DependentFieldPanelType.EDIT) {
      updateDependentFieldFilter(dependentField);
    }
  }
  triggerOpenEvent() {
    const event = new CustomEvent(OPEN_DEPENDENT_FIELDS_EVENT, {
      detail: getPropertyObjectTypeId(this.props.formField)
    });
    window.dispatchEvent(event);
  }
  resetSmartOptions(formField) {
    return formField.set('dependentFieldFilters', []).set('isSmartField', false);
  }
  handleDependentFieldChange({
    target: {
      value
    }
  }) {
    const {
      mainAndQueuedFieldsByPropertyId,
      propertiesById,
      userPreferredLocale
    } = this.props;
    const {
      dependentField,
      isSubmitted
    } = this.state;
    let newDependentFormField = {};
    if (Object.prototype.hasOwnProperty.call(mainAndQueuedFieldsByPropertyId, value)) {
      newDependentFormField = this.resetSmartOptions(mainAndQueuedFieldsByPropertyId[value]);
    } else if (Object.prototype.hasOwnProperty.call(propertiesById, value)) {
      newDependentFormField = createFormFieldWithOptions(propertiesById[value], false, false, userPreferredLocale);
    }
    const newDependentField = updateDependentFieldKey(dependentField, 'dependentFormField', newDependentFormField);
    this.setState({
      dependentField: newDependentField
    }, isSubmitted ? this.validateDependentFieldAndUpdateValidations : null);
  }
  handleDependentFieldFilterChange(key, value) {
    const {
      dependentField,
      isSubmitted
    } = this.state;
    const newDependentField = updateDependentFieldKey(dependentField, 'filters', [createDependentFieldFilter({
      operator: dependentField.filters[0].operator,
      [key]: value
    })]);
    this.setState({
      dependentField: newDependentField
    }, isSubmitted ? this.validateDependentFieldAndUpdateValidations : null);
  }
  handleOperatorChange({
    target: {
      value
    }
  }) {
    const {
      dependentField,
      isSubmitted
    } = this.state;
    const newDependentField = updateDependentFieldKey(dependentField, 'filters', [createDependentFieldFilter({
      operator: value
    })]);
    this.setState({
      dependentField: newDependentField
    }, isSubmitted ? this.validateDependentFieldAndUpdateValidations : null);
  }
  getOperatorSelectOptions() {
    const {
      formField
    } = this.props;
    const fieldOperators = DependentFieldOperatorsByFieldType[formField.fieldType] || DependentFieldOperatorsByFieldType[FieldTypes.TEXT];
    return fieldOperators.map(operator => ({
      value: operator,
      text: getDependentFieldOperatorLabel(operator)
    }));
  }
  isSelectOptionDisabled(formFieldId) {
    const {
      dependentFieldsByPropertyId,
      initialDependentField
    } = this.props;
    const currentDependentFormField = initialDependentField.dependentFormField;
    return (
      // but not the one currently in the store for this dependent field rule
      // is dependent field
      Object.prototype.hasOwnProperty.call(dependentFieldsByPropertyId, formFieldId) && formFieldId !== getPropertyObjectTypeId(currentDependentFormField)
    );
  }
  formFieldsSelectRenderer(props) {
    const {
      mainAndQueuedFieldsByPropertyId
    } = this.props;
    const propertyId = props.option.value;
    if (this.isSelectOptionDisabled(propertyId)) {
      return /*#__PURE__*/_jsx(Tooltip, {
        delay: 200,
        placement: "right",
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: this.getLangKey('disabledFieldAlert')
        }),
        children: props.children
      });
    }
    if (Object.prototype.hasOwnProperty.call(mainAndQueuedFieldsByPropertyId, propertyId)) {
      return /*#__PURE__*/_jsx(Tooltip, {
        delay: 200,
        placement: "right",
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: this.getLangKey('existingFieldAlert')
        }),
        children: props.children
      });
    }
    return props.children;
  }
  getFormFieldsSelectOptions() {
    const {
      propertiesById,
      isFormLabelPropertyEnabled,
      mainAndQueuedFieldsByPropertyId
    } = this.props;
    return Object.values(propertiesById || {}).filter(property => Object.keys(mainAndQueuedFieldsByPropertyId || {}).filter(presentField => presentField === property.id).length === 0).map(property => ({
      text: isFormLabelPropertyEnabled ? `${property.label} (${property.name})` : property.label,
      value: property.id,
      disabled: this.isSelectOptionDisabled(property.id)
    }));
  }
  maybeRenderComparisonInputs() {
    const formField = new Property(this.props.formField);
    const {
      validation
    } = this.state;
    const {
      disableFields
    } = this.props;
    const filter = this.state.dependentField.filters[0];
    const formFieldOptions = formField.options || [];
    if (filter.operator === DependentFieldOperator.IS_NOT_EMPTY) {
      return null;
    }
    let input;
    switch (formField.type) {
      case PropertyType.ENUMERATION:
        {
          const options = formFieldOptions.map(option => {
            const {
              label,
              value
            } = option;
            return {
              value,
              text: decodeHTML(label)
            };
          });
          input = /*#__PURE__*/_jsx(Select, {
            options: options,
            multi: true,
            buttonUse: "form-on-dark",
            anchorType: "button",
            onChange: ({
              target: {
                value
              }
            }) => this.handleDependentFieldFilterChange('strValues', value),
            value: filter.strValues,
            disabled: disableFields,
            "data-test-id": "text-input-progressive-form-field"
          });
          break;
        }
      case PropertyType.DATE:
      case PropertyType.DATE_TIME:
        switch (filter.operator) {
          case DependentFieldOperator.BETWEEN:
          case DependentFieldOperator.NOT_BETWEEN:
            input = /*#__PURE__*/_jsx(DateRange, {
              use: "on-dark",
              showPresets: false,
              disabled: disableFields,
              value: Object.assign({}, filter.strValues[0] && {
                startDate: fromString(filter.strValues[0], DATE_FIELD_FORMAT)
              }, filter.strValues[1] && {
                endDate: fromString(filter.strValues[1], DATE_FIELD_FORMAT)
              }),
              onChange: evt => {
                const {
                  startDate,
                  endDate
                } = evt.target.value;
                this.handleDependentFieldFilterChange('strValues', [toFormattedString(startDate, DATE_FIELD_FORMAT), toFormattedString(endDate, DATE_FIELD_FORMAT)]);
              }
            });
            break;
          case DependentFieldOperator.WITHIN_TIME:
          case DependentFieldOperator.WITHIN_TIME_REVERSE:
            input = /*#__PURE__*/_jsxs(Flex, {
              alignContent: "center",
              gap: "200",
              children: [/*#__PURE__*/_jsx(Box, {
                children: /*#__PURE__*/_jsx(NumberInput, {
                  disabled: disableFields,
                  onChange: ({
                    target: {
                      value
                    }
                  }) => this.handleDependentFieldFilterChange('strValues', [value].concat(filter.strValues.slice(1))),
                  defaultValue: filter.strValues[0] || '',
                  use: "on-dark"
                })
              }), /*#__PURE__*/_jsx(Box, {
                children: /*#__PURE__*/_jsx(Select, {
                  disabled: disableFields,
                  options: [{
                    text: I18n.text(this.getLangKey('daysAgo')),
                    value: 'd'
                  }, {
                    text: I18n.text(this.getLangKey('weeksAgo')),
                    value: 'w'
                  }],
                  buttonUse: "form-on-dark",
                  onChange: ({
                    target: {
                      value
                    }
                  }) => {
                    const newValue = isEmpty(filter.strValues[0]) ? [''].concat([value]) : filter.strValues.slice(0, 1).concat([value]);
                    this.handleDependentFieldFilterChange('strValues', newValue);
                  },
                  value: filter.strValues[1]
                })
              })]
            });
            break;
          default:
            input = /*#__PURE__*/_jsx(DateInput, {
              disabled: disableFields,
              format: getFormatTextFromMetaData(this.props.formField),
              onChange: ({
                target: {
                  value
                }
              }) => {
                this.handleDependentFieldFilterChange('strValue', value ? toFormattedString(value, DATE_FIELD_FORMAT) : undefined);
              },
              value: filter.strValue ? fromString(filter.strValue, DATE_FIELD_FORMAT) : null,
              use: "on-dark"
            });
        }
        break;
      case PropertyType.NUMBER:
        switch (filter.operator) {
          case DependentFieldOperator.BETWEEN:
          case DependentFieldOperator.NOT_BETWEEN:
            input = /*#__PURE__*/_jsxs(Flex, {
              alignContent: "center",
              gap: "200",
              children: [/*#__PURE__*/_jsx(Box, {
                grow: 1,
                children: /*#__PURE__*/_jsx(NumberInput, {
                  disabled: disableFields,
                  onChange: ({
                    target: {
                      value
                    }
                  }) => this.handleDependentFieldFilterChange('numberValues', [Number(value)].concat(filter.numberValues.slice(1))),
                  defaultValue: filter.numberValues[0] || '',
                  use: "on-dark"
                })
              }), /*#__PURE__*/_jsx(Box, {
                grow: 1,
                children: /*#__PURE__*/_jsx(NumberInput, {
                  disabled: disableFields,
                  onChange: ({
                    target: {
                      value
                    }
                  }) => this.handleDependentFieldFilterChange('numberValues', filter.numberValues.slice(0, 1).concat([Number(value)])),
                  defaultValue: filter.numberValues[1] || '',
                  use: "on-dark"
                })
              })]
            });
            break;
          default:
            input = /*#__PURE__*/_jsx(NumberInput, {
              disabled: disableFields,
              onChange: ({
                target: {
                  value
                }
              }) => this.handleDependentFieldFilterChange('numberValue', Number(value)),
              defaultValue: filter.numberValue,
              use: "on-dark",
              "data-test-id": "text-input-progressive-form-field"
            });
        }
        break;
      case PropertyType.STRING:
        input = /*#__PURE__*/_jsx(TextInput, {
          disabled: disableFields,
          onChange: ({
            target: {
              value
            }
          }) => this.handleDependentFieldFilterChange('strValue', value),
          defaultValue: filter.strValue,
          use: "on-dark",
          "data-test-id": "text-input-string-progressive-form-field"
        });
        break;
      default:
    }
    return /*#__PURE__*/_jsx(FormControl, {
      error: validation[DependentFieldValidationType.VALUE_REQUIRED],
      validationMessage: validation[DependentFieldValidationType.VALUE_REQUIRED] ? /*#__PURE__*/_jsx(FormattedMessage, {
        message: "editor.validation.genericRequired"
      }) : null,
      children: input
    });
  }
  render() {
    const {
      type,
      formField,
      cancelAddDependentFieldFilter,
      totalDependentFields,
      initialDependentField,
      disableFields
    } = this.props;
    const {
      dependentField,
      validation
    } = this.state;
    const filter = dependentField[DependentFieldKeys.FILTERS][0];
    const selectOptions = this.getFormFieldsSelectOptions() || [];
    const stripped = selectOptions.map(option => {
      const copy = Object.assign({}, option);
      copy.text = sanitize(option.text, config.TEXTONLY);
      return copy;
    });
    const initialOption = initialDependentField.dependentFormField.name ? getPropertyObjectTypeId(initialDependentField.dependentFormField) : null;
    return /*#__PURE__*/_jsxs("div", {
      "data-test-id": "dependent-fields-configuration-new-panel",
      children: [/*#__PURE__*/_jsx(FormLabel, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: this.getLangKey('if'),
          options: {
            formFieldName: sanitize(formField.name, config.TEXTONLY)
          }
        })
      }), /*#__PURE__*/_jsx(FormControl, {
        error: validation[DependentFieldValidationType.OPERATOR_REQUIRED],
        "data-test-id": "dependent-fields-configuration-operator-required-error",
        validationMessage: validation[DependentFieldValidationType.OPERATOR_REQUIRED] ? /*#__PURE__*/_jsx(FormattedMessage, {
          message: "editor.validation.genericRequired"
        }) : null,
        children: /*#__PURE__*/_jsx(Select, {
          placeholder: "condition",
          buttonUse: "form-on-dark",
          options: this.getOperatorSelectOptions(),
          onChange: this.handleOperatorChange,
          value: filter.operator,
          disabled: disableFields,
          "data-test-id": "if-dependent-form-field"
        })
      }), this.maybeRenderComparisonInputs(), /*#__PURE__*/_jsx(Spacer, {
        space: "400"
      }), /*#__PURE__*/_jsx(FormControl, {
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: this.getLangKey('thenShow')
        }),
        error: validation[DependentFieldValidationType.FIELD_REQUIRED],
        validationMessage: validation[DependentFieldValidationType.FIELD_REQUIRED] ? /*#__PURE__*/_jsx(FormattedMessage, {
          message: "editor.validation.genericRequired"
        }) : null,
        children: /*#__PURE__*/_jsx(Select, {
          placeholder: /*#__PURE__*/_jsx(FormattedMessage, {
            message: this.getLangKey('selectFormField')
          }),
          itemComponent: this.formFieldsSelectRenderer,
          options: stripped,
          buttonUse: "form-on-dark",
          onChange: this.handleDependentFieldChange,
          defaultValue: initialOption,
          disabled: disableFields,
          "data-test-id": "then-show-dependent-form-field"
        })
      }), /*#__PURE__*/_jsx(Spacer, {
        space: "800"
      }), /*#__PURE__*/_jsxs(Flex, {
        gap: "200",
        children: [/*#__PURE__*/_jsx(Box, {
          children: /*#__PURE__*/_jsx(Button, {
            use: "tertiary",
            disabled: disableFields,
            onClick: this.handleAddDependentField,
            "data-test-id": "add-update-dependent-form-field",
            children: type === DependentFieldPanelType.NEW ? /*#__PURE__*/_jsxs(Fragment, {
              children: [/*#__PURE__*/_jsx(FormattedMessage, {
                message: 'add'
              }), disableFields && /*#__PURE__*/_jsx(LockBadge, {})]
            }) : /*#__PURE__*/_jsxs(Fragment, {
              children: [/*#__PURE__*/_jsx(FormattedMessage, {
                message: 'update'
              }), disableFields && /*#__PURE__*/_jsx(LockBadge, {})]
            })
          })
        }), totalDependentFields !== 0 && /*#__PURE__*/_jsx(Box, {
          children: /*#__PURE__*/_jsx(Button, {
            use: "tertiary-light",
            disabled: this.props.disableFields,
            onClick: cancelAddDependentFieldFilter,
            "data-test-id": "cancel-dependent-form-field",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: 'cancel'
            })
          })
        })]
      })]
    });
  }
}
DependentFieldsConfigurationNewPanel.propTypes = {
  type: PropTypes.oneOf(Object.keys(DependentFieldPanelType)),
  initialDependentField: PropTypes.object.isRequired,
  formField: PropTypes.object.isRequired,
  mainAndQueuedFieldsByPropertyId: PropTypes.object.isRequired,
  dependentFieldsByPropertyId: PropTypes.object.isRequired,
  propertiesById: PropTypes.object.isRequired,
  addDependentFieldFilter: PropTypes.func.isRequired,
  updateDependentFieldFilter: PropTypes.func.isRequired,
  cancelAddDependentFieldFilter: PropTypes.func.isRequired,
  isFormLabelPropertyEnabled: PropTypes.bool.isRequired,
  totalDependentFields: PropTypes.number.isRequired,
  disableFields: PropTypes.bool,
  userPreferredLocale: PropTypes.string
};
export default DependentFieldsConfigurationNewPanel;